import { useState, useCallback, useRef } from 'react';
import FinancingService from '../services/FinancingService';
import Helpers from '../../../../helpers';

type OrderId = string | number;

export interface UseOrderFinancingResult {
  showFinanceDialog: boolean;
  financeLoading: boolean;
  financeError: string | null;
  financingEnabled: boolean | undefined;
  shouldShowFinancing: (financialStatus: string | null | undefined) => boolean;
  getFinanceButtonText: (financialStatus: string | null | undefined) => string;
  getFinancialStatusColor: (status: string) => string;
  initializeFinancing: (showDashboard?: boolean) => Promise<void>;
  getFinancing: () => Promise<void>;
  financeOrders: () => Promise<void>;
  setFinanceSelected: (orderIds: OrderId[] | null | undefined) => void;
  setSelectedForFinancing: (orderIds: OrderId[] | null | undefined) => void;
  clearFinanceError: () => void;
  toggleFinanceDialog: (show: boolean) => void;
}

export function useOrderFinancing(financialStatus: string | null | undefined, financingEnabled?: boolean): UseOrderFinancingResult {
  const [showFinanceDialog, setShowFinanceDialog] = useState(false);
  const [financeLoading, setFinanceLoading] = useState(false);
  const [financeError, setFinanceError] = useState<string | null>(null);

  const financeSelectedRef = useRef<OrderId[]>([]);

  // Get financial status color
  const getFinancialStatusColor = useCallback((status: string) => {
    return FinancingService.getFinancialStatusColor(status);
  }, []);

  // Get financing
  const getFinancing = useCallback(async () => {
    try {
      setFinanceLoading(true);
      setFinanceError(null);

      const connectToken = await FinancingService.getConnectToken();
      FinancingService.initializeKanmonConnect(connectToken);
    } catch (error: any) {
      console.error('Error getting financing:', error);
      setFinanceError(error.message);
    } finally {
      setFinanceLoading(false);
    }
  }, []);

  // Initialize financing
  const initializeFinancing = useCallback(async (showDashboard = true) => {
    try {
      setFinanceLoading(true);
      setFinanceError(null);

      if (financialStatus === "ACCEPTED") {
        const connectToken = await FinancingService.getConnectToken();
        FinancingService.initializeKanmonConnect(connectToken, showDashboard);
        setShowFinanceDialog(true);
      } else {
        await getFinancing();
      }
    } catch (error: any) {
      console.error('Error initializing financing:', error);
      setFinanceError(error.message);
    } finally {
      setFinanceLoading(false);
    }
  }, [financialStatus, getFinancing]);

  // Finance selected orders
  const financeOrders = useCallback(async () => {
    try {
      setFinanceLoading(true);
      setFinanceError(null);

      if (!financeSelectedRef.current || financeSelectedRef.current.length === 0) {
        throw new Error('Please select orders to finance');
      }

      const sessionToken = await FinancingService.financeOrders(
        financeSelectedRef.current
      );
      FinancingService.showFinancingFlow(sessionToken);
    } catch (error: any) {
      console.error('Error financing orders:', error);

      let errorMessage = 'An unexpected error occurred.';
      if (error.response && error.response.data) {
        const helpers = new Helpers();
        errorMessage = helpers.getApiErrors(error.response.data).join('\n');
      } else if (error.message) {
        errorMessage = error.message;
      }

      setFinanceError(errorMessage);
    } finally {
      setFinanceLoading(false);
    }
  }, []);

  // Set selected orders for financing
  const setSelectedForFinancing = useCallback((orderIds: OrderId[] | null | undefined) => {
    financeSelectedRef.current = orderIds || [];
  }, []);

  // Clear financing error
  const clearFinanceError = useCallback(() => {
    setFinanceError(null);
  }, []);

  // Toggle finance dialog
  const toggleFinanceDialog = useCallback((show: boolean) => {
    setShowFinanceDialog(show);
  }, []);

  return {
    showFinanceDialog,
    financeLoading,
    financeError,
    financingEnabled,
    shouldShowFinancing: FinancingService.shouldShowFinancing,
    getFinanceButtonText: FinancingService.getFinanceButtonText,
    getFinancialStatusColor,
    initializeFinancing,
    getFinancing,
    financeOrders,
    setFinanceSelected: setSelectedForFinancing,
    setSelectedForFinancing,
    clearFinanceError,
    toggleFinanceDialog
  };
}